import { Editor } from "@tiptap/core";
import { KeyboardEvent } from "react";

import {
  isArrowDownEvent,
  isArrowUpEvent,
  isBackSpaceEvent,
  isEnterEvent,
  isShiftEnterEvent,
} from "@helpers/helpers";

export const handleKeyDownEvent = ({
  e,
  editor,
  getPos,
  onDelete,
  onExpandArtifactDescription,
}: {
  e: KeyboardEvent<Element> | undefined;
  editor: Editor;
  getPos: () => number;
  onDelete: (callback?: () => void) => void;
  onExpandArtifactDescription: () => void;
}) => {
  if (!e) return;
  const target = e.target as HTMLElement & { editor: Editor };
  const { editor: artifactEditor } = target;
  const { selection } = artifactEditor.state;
  const { from, to } = selection;
  // const pos = getPos();

  // in case user has focused directly on the artifact editor and bypassing the topic tiptap editor
  // if (editor.state.selection.from !== pos) {
  //   editor.chain().focus(getPos()).run();
  // }

  // press enter go to next node in editor
  if (isShiftEnterEvent(e)) {
    e.stopPropagation();
    e.preventDefault();
    onExpandArtifactDescription();
    return;
  }

  if (isEnterEvent(e)) {
    e.stopPropagation();
    e.preventDefault();
    const isInList =
      editor.isActive("bulletList") || editor.isActive("orderedList");

    const commandChain = editor
      .chain()
      .focus(getPos() + 1)
      .insertContent("<p></p>");
    if (isInList) {
      commandChain.wrapInList("listItem").run();
    } else {
      commandChain.run();
    }
  }

  // press arrow down on last letter in input, go to next node in editor
  if (isArrowDownEvent(e)) {
    const cursorPosition = target.editor.state.selection.$anchor.pos;
    if (cursorPosition >= target.editor.getText().length) {
      e.stopPropagation();
      e.preventDefault();
      // when next node uses a gap cursor we increment one less to show gap cursor
      const nodeAfter = editor.state.doc.resolve(getPos() + 1).nodeAfter;
      const incrementPosition =
        nodeAfter && nodeAfter.type.spec.allowGapCursor ? 1 : 2;
      editor
        .chain()
        .focus(getPos() + incrementPosition) // when another artifact
        .run();
    }
  }

  if (isArrowUpEvent(e)) {
    const cursorPosition = target.editor.state.selection.$anchor.pos;
    if (cursorPosition === 0) {
      e.stopPropagation();
      e.preventDefault();
      const nodeBefore = editor.state.doc.resolve(getPos()).nodeBefore;
      const decrementPosition =
        nodeBefore && nodeBefore.type.spec.allowGapCursor ? 0 : 1;
      editor
        .chain()
        .focus(getPos() - decrementPosition)
        .run();
    }
  }

  if (isBackSpaceEvent(e) && from === 0 && to === 0) {
    onDelete(() => {
      editor
        .chain()
        .focus(getPos() - 1)
        .run();
    });
  }
};

export const deleteArtifact = ({
  artifact,
  deleteNode,
  callback,
}: {
  artifact?: {
    id: number;
  };
  deleteNode: () => void;
  callback?: () => void;
}) => {
  if (artifact?.id) {
    deleteNode();
    if (callback) callback();
    return;
  }
  deleteNode();
  if (callback) callback();
};
